import { type ReactElement, useEffect } from 'react';
import { Routes, useNavigate, Route } from 'react-router';

import { MainLayout } from 'src/App/Layouts/MainLayout/MainLayout';
import { AddUserPage } from 'src/App/Pages/AddUserPage/AddUserPage';
import { EditUserDetailsPage } from 'src/App/Pages/EditUserDetailsPage/EditUserDetailsPage';
import { UsersListPage } from 'src/App/Pages/UsersListPage/UsersListPage';
import {
  URL_ROOT,
  URL_ADD_USER,
  URL_EDIT_USER_DETAILS,
  URL_ERROR_FORBIDDEN,
} from 'src/constants/urls';
import { ROLE_ADMIN } from 'src/constants/user';
import type { CurrentAdvertiserContext } from 'src/graphql/generated';
import { useAccountContext } from 'src/hooks/use-account-context';
import { useLoggedInUser } from 'src/hooks/use-logged-in-user';
import { useConfig } from 'src/modules/ConfigProvider';
import { getCountryFromSite } from 'src/utils/getCountryFromSite';
import { initialiseLogger } from 'src/utils/logger/logger';

export const Root = () => {
  const { environment, appVersion, site, urlResolver } = useConfig();

  const { data: accountContext } = useAccountContext();
  const currentAdvertiser = accountContext?.currentAdvertiser as
    | CurrentAdvertiserContext
    | undefined;
  const hirerAccountId = currentAdvertiser?.seekId;

  const currentUserId = accountContext?.headerFooterContext?.user?.id;

  const country = getCountryFromSite(site);

  initialiseLogger({
    buildVersion: appVersion,
    environment,
    hirerAccountId,
    userId: currentUserId,
    country,
  });

  return (
    <MainLayout>
      <Routes>
        <Route
          path={urlResolver({ path: URL_ROOT })}
          element={<ProtectedRoute element={<UsersListPage />} />}
        />
        <Route
          path={urlResolver({ path: URL_EDIT_USER_DETAILS })}
          element={<ProtectedRoute element={<EditUserDetailsPage />} />}
        />
        <Route
          path={urlResolver({ path: URL_ADD_USER })}
          element={<ProtectedRoute element={<AddUserPage />} />}
        />
      </Routes>
    </MainLayout>
  );
};

const ProtectedRoute = ({ element }: { element: ReactElement }) => {
  const navigate = useNavigate();
  const { environment } = useConfig();
  const {
    data: userData,
    loading: userLoading,
    error: userError,
  } = useLoggedInUser();

  useEffect(() => {
    if (!userError && !userLoading && userData) {
      if (userData?.role !== ROLE_ADMIN) {
        navigate(URL_ERROR_FORBIDDEN, { replace: true });
      }
    }
  }, [navigate, userData, userError, userLoading, environment]);

  if (userLoading) {
    return null;
  }

  return <>{element}</>;
};
